// ChannelSelector Component - 2ECOMP Fleet Management
// Camera channel selection with visual indicators

import React, { useCallback } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Badge,
  Stack,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VideocamIcon from '@mui/icons-material/Videocam';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { CHANNEL_NAMES, CHANNEL_ICONS } from '../utils/cameraConstants';

// Icon mapping
const IconComponents = {
  DirectionsCar: DirectionsCarIcon,
  Person: PersonIcon,
  ChevronLeft: ChevronLeftIcon,
  ChevronRight: ChevronRightIcon,
  Visibility: VisibilityIcon,
  Videocam: VideocamIcon,
};

// Styled Components
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  '& .MuiToggleButtonGroup-grouped': {
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
      borderLeft: `1px solid ${theme.palette.divider}`,
      marginLeft: 0,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

const ChannelButton = styled(ToggleButton)(({ theme, active, available }) => ({
  padding: theme.spacing(0.5, 1),
  minWidth: 'auto',
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  transition: 'all 0.2s ease',
  opacity: available ? 1 : 0.5,
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&:hover': {
    backgroundColor: available
      ? alpha(theme.palette.primary.main, 0.1)
      : theme.palette.action.disabledBackground,
  },
}));

const CompactChannelButton = styled(ToggleButton)(({ theme, active, available }) => ({
  padding: theme.spacing(0.25, 0.75),
  minWidth: 32,
  height: 28,
  fontSize: '0.75rem',
  borderRadius: theme.shape.borderRadius,
  opacity: available ? 1 : 0.5,
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const ActiveIndicator = styled(FiberManualRecordIcon)(({ theme }) => ({
  position: 'absolute',
  top: 2,
  right: 2,
  fontSize: 8,
  color: theme.palette.success.main,
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.4 },
    '100%': { opacity: 1 },
  },
}));

/**
 * ChannelSelector - Component for selecting camera channels
 *
 * @param {Object} props
 * @param {number} props.selectedChannel - Currently selected channel
 * @param {number[]} props.activeChannels - Array of channels with active streams
 * @param {number[]} props.availableChannels - Array of available channels (defaults to 1-8)
 * @param {function} props.onChange - Callback when channel changes
 * @param {boolean} props.showLabels - Show channel labels
 * @param {boolean} props.showIcons - Show channel icons
 * @param {boolean} props.compact - Compact mode
 * @param {boolean} props.showInactive - Show inactive channels (grayed out)
 * @param {Object} props.sx - Additional MUI sx props
 */
const ChannelSelector = ({
  selectedChannel,
  activeChannels = [],
  availableChannels = [1, 2, 3, 4, 5, 6, 7, 8],
  onChange,
  showLabels = true,
  showIcons = true,
  compact = false,
  showInactive = true,
  sx = {},
}) => {
  // Handle channel change
  const handleChange = useCallback((event, newChannel) => {
    if (newChannel !== null && onChange) {
      onChange(newChannel);
    }
  }, [onChange]);

  // Get channels to display
  const displayChannels = showInactive
    ? availableChannels
    : availableChannels.filter(ch => activeChannels.includes(ch));

  // Get icon component for channel
  const getIcon = (channel) => {
    const iconName = CHANNEL_ICONS[channel];
    const IconComponent = IconComponents[iconName] || VideocamIcon;
    return <IconComponent fontSize="small" />;
  };

  // Check if channel is active (streaming)
  const isActive = (channel) => activeChannels.includes(channel);

  if (displayChannels.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2, ...sx }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma câmera ativa
        </Typography>
      </Box>
    );
  }

  // Compact mode - numbers only
  if (compact) {
    return (
      <StyledToggleButtonGroup
        value={selectedChannel}
        exclusive
        onChange={handleChange}
        size="small"
        sx={sx}
      >
        {displayChannels.map((channel) => (
          <Tooltip
            key={channel}
            title={`${CHANNEL_NAMES[channel] || `Canal ${channel}`}${isActive(channel) ? ' (Ativo)' : ''}`}
          >
            <CompactChannelButton
              value={channel}
              available={isActive(channel) ? 1 : 0}
            >
              {channel}
              {isActive(channel) && <ActiveIndicator />}
            </CompactChannelButton>
          </Tooltip>
        ))}
      </StyledToggleButtonGroup>
    );
  }

  // Full mode with icons and/or labels
  return (
    <StyledToggleButtonGroup
      value={selectedChannel}
      exclusive
      onChange={handleChange}
      size="small"
      sx={sx}
    >
      {displayChannels.map((channel) => (
        <Tooltip
          key={channel}
          title={`${CHANNEL_NAMES[channel] || `Canal ${channel}`}${isActive(channel) ? ' - Transmitindo' : ''}`}
        >
          <ChannelButton
            value={channel}
            available={isActive(channel) ? 1 : 0}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              {showIcons && getIcon(channel)}
              {showLabels && (
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {compact ? channel : CHANNEL_NAMES[channel] || `CH${channel}`}
                </Typography>
              )}
              {!showLabels && !showIcons && channel}
            </Stack>
            {isActive(channel) && <ActiveIndicator />}
          </ChannelButton>
        </Tooltip>
      ))}
    </StyledToggleButtonGroup>
  );
};

/**
 * ChannelGrid - Grid layout for channel selection
 * Better for displaying many channels at once
 */
export const ChannelGrid = ({
  selectedChannel,
  activeChannels = [],
  onChange,
  columns = 4,
  sx = {},
}) => {
  const theme = useTheme();
  const channels = [1, 2, 3, 4, 5, 6, 7, 8];

  const handleClick = (channel) => {
    if (onChange) {
      onChange(channel);
    }
  };

  const isActive = (channel) => activeChannels.includes(channel);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 1,
        ...sx,
      }}
    >
      {channels.map((channel) => {
        const IconComponent = IconComponents[CHANNEL_ICONS[channel]] || VideocamIcon;
        const active = isActive(channel);
        const selected = selectedChannel === channel;

        return (
          <Box
            key={channel}
            onClick={() => handleClick(channel)}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: 1,
              borderColor: selected ? 'primary.main' : 'divider',
              backgroundColor: selected
                ? 'primary.main'
                : active
                  ? alpha(theme.palette.success.main, 0.1)
                  : 'background.paper',
              color: selected ? theme.palette.primary.contrastText : 'text.primary',
              cursor: 'pointer',
              opacity: active ? 1 : 0.5,
              transition: 'all 0.2s ease',
              textAlign: 'center',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: selected
                  ? 'primary.dark'
                  : alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <Badge
              variant="dot"
              color="success"
              invisible={!active}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <IconComponent
                sx={{
                  fontSize: 24,
                  mb: 0.5,
                  color: selected ? theme.palette.primary.contrastText : active ? 'success.main' : 'text.disabled',
                }}
              />
            </Badge>
            <Typography
              variant="caption"
              display="block"
              sx={{
                fontWeight: selected ? 600 : 400,
                fontSize: '0.65rem',
              }}
            >
              {CHANNEL_NAMES[channel] || `Canal ${channel}`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChannelSelector;
